/**
 * Shared helpers for surfacing backend error responses in the UI.
 *
 * The backend always returns errors inside the standardised APIResponse
 * envelope:
 *
 *   { status: <int>, message: <str>, result: <errors|null> }
 *
 * For DRF validation errors `result` is a dict shaped like
 * `{ field_name: ["msg", ...], non_field_errors: ["..."] }` and `message`
 * is the generic "Validation failed." string.
 *
 * For ServiceError / business-rule errors `result` is `null` and `message`
 * holds the human-readable detail (e.g. "This warranty has already used
 * its once-per-lifetime transfer.").
 *
 * Frontend code historically toasted hardcoded fallbacks ("Failed to save
 * X") and threw away the BE message. `extractApiErrorMessage` reverses
 * that: given an axios error / response payload / arbitrary object, it
 * returns the most useful message we can find for the user.
 *
 * Use `toastApiError(err, fallback)` as a one-liner inside `catch` and
 * mutation `onError` callbacks. It will:
 *   - prefer DRF field/non-field validation errors,
 *   - fall back to the envelope `message`,
 *   - then to any nested `detail`/`error`/`errors` strings,
 *   - and finally to the supplied fallback when nothing useful is found.
 */
import i18n from 'i18next'
import { toast } from 'sonner'

import { TOAST_DURATION } from './toast-duration'
import { titleCaseKey } from './humanise'

const ENVELOPE_GENERIC_MESSAGES = new Set([
  'validation failed.',
  'validation failed',
  'a service error occurred.',
  'a service error occurred',
  'an error occurred.',
  'an error occurred',
])

const FIELD_ERROR_KEYS_TO_SKIP = new Set([
  'status',
  'status_code',
  'code',
  'request_id',
])

interface ExtractOptions {
  /**
   * When the envelope `message` is one of the generic placeholders
   * ("Validation failed.", "An error occurred.") we drop it in favour of
   * the field-level errors. Set to false to always preserve the message.
   */
  preferFieldErrors?: boolean
  /**
   * Maximum number of field errors to concatenate into a single toast.
   * Extra entries are summarised as "+N more".
   */
  maxFieldErrors?: number
}

/**
 * Try to pull the most useful human-readable message out of a backend
 * error response, an axios error, or any arbitrary unknown value.
 * Returns `null` when nothing usable is found.
 */
export function extractApiErrorMessage(
  value: unknown,
  options: ExtractOptions = {},
): string | null {
  const message = extractMessage(value, options)
  // A body over the proxy's size limit never reaches the API, so its 413 is a
  // proxy HTML page with no message in it.
  if (message === null && responseStatus(value) === 413) {
    return i18n.t('files.tooLarge', 'This file is too large to upload.')
  }
  return message
}

function responseStatus(value: unknown): unknown {
  return (value as { response?: { status?: unknown } } | null)?.response?.status
}

function extractMessage(
  value: unknown,
  options: ExtractOptions,
): string | null {
  const { preferFieldErrors = true, maxFieldErrors = 4 } = options

  const payload = unwrapToErrorPayload(value)
  if (payload === null) return null

  if (typeof payload === 'string') {
    const trimmed = payload.trim()
    if (trimmed.length === 0) return null
    // An HTML body is an error PAGE, not an error message - a debug traceback,
    // a proxy 502, a login redirect. Showing it verbatim is how a Django stack
    // trace ended up in front of a customer.
    if (looksLikeMarkup(trimmed)) {
      console.error('[api] non-JSON error body', trimmed.slice(0, 2000))
      return null
    }
    // A real message is a sentence. Past this length it is a dump of something,
    // and a toast is the wrong place for it.
    if (trimmed.length > MAX_MESSAGE_LENGTH) {
      console.error('[api] oversized error body', trimmed.slice(0, 2000))
      return null
    }
    return trimmed
  }

  if (Array.isArray(payload)) {
    return collectListMessage(payload, maxFieldErrors)
  }

  if (typeof payload !== 'object') return null

  const record = payload as Record<string, unknown>

  // Standardised envelope { status, message, result }
  if ('message' in record || 'result' in record) {
    const envelopeMessage = stringOrNull(record.message)
    const result = record.result

    if (preferFieldErrors) {
      const fieldMessage = formatFieldErrors(result, maxFieldErrors)
      if (fieldMessage) return fieldMessage
    }

    if (envelopeMessage && !isGenericMessage(envelopeMessage)) {
      return envelopeMessage
    }

    if (!preferFieldErrors) {
      const fieldMessage = formatFieldErrors(result, maxFieldErrors)
      if (fieldMessage) return fieldMessage
    }

    if (envelopeMessage) return envelopeMessage
  }

  // Plain DRF-style payload (no envelope) - try common shapes first.
  for (const key of ['detail', 'message', 'error', 'non_field_errors']) {
    const nested = extractApiErrorMessage(record[key], options)
    if (nested) return nested
  }

  const errorsField = record.errors
  if (errorsField !== undefined) {
    const fieldMessage = formatFieldErrors(errorsField, maxFieldErrors)
    if (fieldMessage) return fieldMessage
    const nested = extractApiErrorMessage(errorsField, options)
    if (nested) return nested
  }

  // Last resort: walk the rest of the object looking for any string.
  const fieldMessage = formatFieldErrors(record, maxFieldErrors)
  if (fieldMessage) return fieldMessage

  return null
}

/**
 * Convenience: show the BE error message in a toast, falling back to the
 * provided message when the BE didn't send anything useful.
 *
 * Returns the string that was toasted so callers can also log / inspect.
 *
 * The toast stays until dismissed (WPS-97). This is the shared path for
 * every backend failure in the product, and a failure message that
 * auto-dismisses after a few seconds is one the user cannot act on.
 */
export function toastApiError(
  err: unknown,
  fallback: string,
  options?: ExtractOptions,
): string {
  const message = extractApiErrorMessage(err, options) ?? fallback
  toast.error(message, { duration: TOAST_DURATION.error })
  return message
}

/**
 * Details of a 409 raised when a dynamic attribute still has recorded values.
 *
 * Every attribute-delete endpoint (transactions, hosts, warranties, workflows,
 * claims, products, entities) returns the same
 * `result.code === 'attribute_has_values'` shape, so the delete UI can offer a
 * uniform "delete anyway" (force) confirmation.
 */
export interface AttributeHasValuesInfo {
  count: number
  noun: string
  nounPlural: string
  message: string
}

/**
 * Return the force-deletable details when `err` is the "attribute has recorded
 * values" 409, or `null` for any other error. A non-null result means the
 * caller may retry the delete with `force: true` to cascade the values.
 */
export function getAttributeHasValuesError(
  err: unknown,
): AttributeHasValuesInfo | null {
  const payload = unwrapToErrorPayload(err)
  if (!payload || typeof payload !== 'object') return null

  const record = payload as Record<string, unknown>
  const result = record.result
  if (!result || typeof result !== 'object') return null

  const detail = result as Record<string, unknown>
  if (detail.code !== 'attribute_has_values') return null

  const count = typeof detail.count === 'number' ? detail.count : 0
  const noun = typeof detail.noun === 'string' ? detail.noun : 'record'
  const nounPlural =
    typeof detail.noun_plural === 'string' ? detail.noun_plural : `${noun}s`
  const message =
    stringOrNull(record.message) ?? 'This attribute has recorded values.'

  return { count, noun, nounPlural, message }
}

/** One value that could not be loaded into stock, and why. */
export interface ValueStockRejectionInfo {
  value: string
  reason: string
  usedBy: { entity: string; id: string } | null
  scope: { entity: string; id: string } | null
}

/**
 * Return the per-value reasons when `err` is the value-stock 409, or `null`.
 *
 * Same `result.code` shape as `getAttributeHasValuesError`. A count alone is
 * unusable on a paste of several thousand values: the admin needs the values
 * themselves to act, and for an `other_scope` rejection, which product or
 * variant is already holding them.
 */
export function getValueStockRejections(
  err: unknown,
): ValueStockRejectionInfo[] | null {
  const payload = unwrapToErrorPayload(err)
  if (!payload || typeof payload !== 'object') return null

  const result = (payload as Record<string, unknown>).result
  if (!result || typeof result !== 'object') return null

  const detail = result as Record<string, unknown>
  if (detail.code !== 'value_stock_conflict') return null
  if (!Array.isArray(detail.rejected)) return null

  const rows: ValueStockRejectionInfo[] = []
  for (const raw of detail.rejected) {
    if (!raw || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    const value = stringOrNull(row.value)
    if (!value) continue
    rows.push({
      value,
      reason: stringOrNull(row.reason) ?? 'unavailable',
      usedBy: asEntityRef(row.used_by),
      scope: asEntityRef(row.scope),
    })
  }
  return rows.length > 0 ? rows : null
}

function asEntityRef(value: unknown): { entity: string; id: string } | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const entity = stringOrNull(record.entity)
  const id = stringOrNull(record.id)
  return entity && id ? { entity, id } : null
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** Longest string still plausibly a human-written message rather than a dump. */
const MAX_MESSAGE_LENGTH = 300

function looksLikeMarkup(text: string): boolean {
  const head = text.slice(0, 200).toLowerCase()
  return (
    head.startsWith('<!doctype') ||
    head.startsWith('<html') ||
    head.includes('<body') ||
    head.includes('<!doctype html')
  )
}

function unwrapToErrorPayload(value: unknown): unknown {
  if (value === null || value === undefined) return null

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    // Axios error → response.data
    const response = record.response as { data?: unknown; status?: number }
      | undefined
    if (response && 'data' in response) {
      // A 5xx body is a server fault, not a message for the person using the
      // app: with DEBUG on it is Django's HTML traceback, and off it is a bare
      // stack or proxy page. Neither belongs in a toast - customers have been
      // shown raw tracebacks this way. Log it for whoever is debugging and let
      // the caller's fallback do the talking.
      if (typeof response.status === 'number' && response.status >= 500) {
        console.error('[api] server error', response.status, response.data)
        return null
      }
      return response.data ?? null
    }
    // Native JS Error / AxiosError without a response - top-level
    // `.message` like "Network Error" or "Request failed with status
    // code 400" is not user-friendly. Bail out so we surface the caller's
    // fallback instead of leaking transport noise into a toast.
    if (value instanceof Error) return null
    // { data: ... } pre-extracted envelope
    if ('data' in record && !('message' in record) && !('result' in record)) {
      return record.data
    }
  }

  return value
}

function isGenericMessage(message: string): boolean {
  return ENVELOPE_GENERIC_MESSAGES.has(message.trim().toLowerCase())
}

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function collectListMessage(list: unknown[], max: number): string | null {
  const messages: string[] = []
  for (const item of list) {
    const msg = extractApiErrorMessage(item, { preferFieldErrors: true })
    if (msg) messages.push(msg)
  }
  if (messages.length === 0) return null
  return joinWithCap(messages, max)
}

function formatFieldErrors(value: unknown, max: number): string | null {
  if (!value || typeof value !== 'object') return null

  if (Array.isArray(value)) {
    return collectListMessage(value, max)
  }

  const record = value as Record<string, unknown>
  const parts: string[] = []

  for (const [key, raw] of Object.entries(record)) {
    if (FIELD_ERROR_KEYS_TO_SKIP.has(key)) continue
    if (key === 'message' || key === 'detail') continue

    const message = extractApiErrorMessage(raw, { preferFieldErrors: true })
    if (!message) continue

    if (key === 'non_field_errors' || key === 'errors' || key.startsWith('__')) {
      parts.push(message)
    } else {
      parts.push(`${titleCaseKey(key)}: ${message}`)
    }
  }

  if (parts.length === 0) return null
  return joinWithCap(parts, max)
}

function joinWithCap(parts: string[], max: number): string {
  if (parts.length <= max) return parts.join('\n')
  const visible = parts.slice(0, max)
  const remaining = parts.length - max
  visible.push(`+${remaining} more`)
  return visible.join('\n')
}

